import { useEffect, useRef, useState } from 'react'
import SelectedCheckmark from './SelectedCheckmark'

// Bound to the real, writable `sell_tax_applied` field -- README.md ("the expanded Projected
// Interest panel") has the mapping. Value/label split for the same reason as InterestDropdown.
// Letter 104 (1.3): "Holding Costs -> Monthly", "Selling Costs -> Paid at Sale", "Refinance Summ."
// removed. Values unchanged. A stored 'cashout' (or any other value) is not an option: it shows and is
// checked as Monthly, the way the backend computes it (reilm-be flip/holdingCosts.js, interest in
// Holding Costs unless 'sell').
export const ALLOCATION_OPTIONS = [
  { value: 'hold', label: 'Monthly' },
  { value: 'sell', label: 'Paid at Sale' },
]

// Closed-pill chevron, apex down (spec 4.2.1), scaled to this control's own smaller glyph.
function Chevron() {
  return (
    <svg width="7" height="4" viewBox="0 0 7 4" fill="none" aria-hidden="true">
      <path d="M0.5 0.5L3.5 3.5L6.5 0.5" stroke="#4F5F4A" />
    </svg>
  )
}

// Trigger geometry is the pill itself (spec 4.2.1: 144x22, rx11, fill #EEF3EC). Letter 99 (7.4.4.1,
// 7.4.4.3): label 12px in normal case with a 20px left inset (was 10px caps, 25.5), which leaves 99px
// for the label -- README.md ("Allocation label and trigger size").
export default function AllocationDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const optionRefs = useRef([])
  const selected = ALLOCATION_OPTIONS.find((o) => o.value === value) ?? ALLOCATION_OPTIONS[0]
  // The option shown as chosen -- checkmark, bold, aria-selected, first focus -- is `selected`'s, so a
  // stored 'cashout' is Monthly everywhere, not an unchecked list.
  const selectedValue = selected.value

  useEffect(() => {
    if (!open) return
    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  // Focuses the selected option the moment the list opens -- same reasoning as InterestDropdown.
  useEffect(() => {
    if (!open) return
    const idx = Math.max(0, ALLOCATION_OPTIONS.findIndex((o) => o.value === selectedValue))
    optionRefs.current[idx]?.focus()
  }, [open, selectedValue])

  const closeAndRefocusTrigger = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  const handleListKeyDown = (e) => {
    const currentIndex = optionRefs.current.findIndex((el) => el === document.activeElement)
    if (e.key === 'Escape') {
      e.preventDefault()
      closeAndRefocusTrigger()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      optionRefs.current[Math.min(ALLOCATION_OPTIONS.length - 1, currentIndex + 1)]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      optionRefs.current[Math.max(0, currentIndex - 1)]?.focus()
    } else if (e.key === 'Tab') {
      closeAndRefocusTrigger()
    }
  }

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        tabIndex={-1}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-between gap-x-[8px] w-[144px] h-[22px] rounded-[11px] bg-[#EEF3EC] hover:bg-[#DDE9D8] pl-[20px] pr-[10px] outline-none focus-visible:ring-2 focus-visible:ring-brand-green"
      >
        <span className="text-[12px] font-semibold tracking-[0.02em] text-brand-secondary whitespace-nowrap">{selected.label}</span>
        <Chevron />
      </button>
      {/* 144 wide, the trigger's own width (letter 99, 7.4.4.2; was 128). Open list per spec 4.3, including its genuinely drawn hover fill (unlike InterestDropdown's,
          which is inferred) -- the export pins a cursor graphic over the second option (then "Selling Costs") as proof.
          Inset `outline`, not `border`: a border on this auto-height, border-box element adds 2px
          on top of its own content+padding, measuring 96 against the drawn 94 (same root cause as
          SectionCard's own border-vs-outline fix). Not applied to InterestDropdown's own list --
          that one's height is NOT SPECIFIED IN FIGMA, a different ruling entirely. */}
      {open ? (
        <ul
          role="listbox"
          aria-label="Interest Payments"
          onKeyDown={handleListKeyDown}
          className="absolute z-20 top-full left-0 mt-[4px] w-[144px] rounded-[8px] bg-white py-[8px]"
          style={{ boxShadow: '0 0 40px rgba(115,147,94,0.15)', outline: '1px solid #C8D2C3', outlineOffset: '-1px' }}
        >
          {ALLOCATION_OPTIONS.map((o, i) => (
            <li key={o.value}>
              <button
                ref={(el) => (optionRefs.current[i] = el)}
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={o.value === selectedValue}
                onClick={() => {
                  onChange(o.value)
                  closeAndRefocusTrigger()
                }}
                className={`w-full flex items-center justify-between gap-x-[6px] text-left px-[16px] h-[26px] text-[12px] text-brand-dark bg-white hover:bg-[#EEF3EC] ${
                  o.value === selectedValue ? 'font-semibold' : ''
                }`}
              >
                {/* 6 to the checkmark, not 8: kept from letter 99, when a selected "Refinance Summ." (95.34
                    at 600) needed 96 of the 144 list -- README.md ("Allocation label and trigger size"). */}
                <span className="whitespace-nowrap">{o.label}</span>
                {o.value === selectedValue ? <SelectedCheckmark /> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
